import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as termsSanityApi from 'api/sanity/terms';
import { AppThunk, RootState } from 'store';
import i18next from 'i18next';
import { getPartnerSettings } from 'utils/partnerSettings';

interface PartnerTermsItem {
  title: string;
  updateTime: string;
  content: SanityRichText[];
  override: string;
  slug: string;
}

interface State {
  loading: boolean;
  partnerTermsLoading: boolean;
  generalTerms: SanityRichText[];
  generalUpdateTime: string;
  generalTermsTitle: string;
  wechoooseTerms: SanityRichText[];
  wechoooseUpdateTime: string;
  wechoooseTermsTitle: string;
  partnerTerms: SanityRichText[];
  partnerUpdateTime: string;
  partnerTermsTitle: string;
  policyTerms: SanityRichText[];
  policyUpdateTime: string;
  policyTermsTitle: string;
  cookieTerms: SanityRichText[];
  cookieUpdateTime: string;
  cookieTermsTitle: string;
  additionalPartnerTerms?: PartnerTermsItem[];
}

const SANITY_TERMS = {
  general: 'general-terms-and-conditions',
  weChooose: 'wechooose-terms-and-conditions',
  policy: 'chooose-data-protection-policy',
  cookie: 'chooose-cookie-policy',
};

const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const replaceVariablesWithFallback = (content: string): string => {
  const data = getPartnerSettings();
  if (!data.feeDisplay?.allowed || !data.feeDisplay?.displayFeeOnTermsAndConditions) {
    data.customerFees = undefined;
  }
  const variableRegex = /{{[a-zA-Z_.0-9 ]*([??]{1}[^]*)?}}/g;
  const variables = new Set(content.match(variableRegex));

  if (variables) {
    variables.forEach(variable => {
      let variableName = variable.replace(/[{}]/g, '');
      let fallbackValue = '';
      if (variableName.includes('??')) {
        const [v, fallback] = variableName.split('??');
        variableName = v?.replace(/\s+/g, '');
        fallbackValue = fallback?.trim().replace(/["']/g, '');
      } else {
        variableName = variableName?.replace(/\s+/g, '');
      }
      let variableValue;
      if (variableName.includes('.')) {
        const nestedVariables = variableName.split('.');
        //@ts-ignore
        variableValue = nestedVariables?.reduce((prev, variable) => prev?.[variable] ?? fallbackValue, data);
      } else {
        //@ts-ignore
        variableValue = data[variableName] ?? fallbackValue;
      }
      if (Array.isArray(variableValue)) {
        variableValue = variableValue.reduce((curr, prev, index, arr) => curr + prev + (index !== arr.length - 1 ? ', ' : ''), '');
      }
      content = content.replace(new RegExp(escapeRegExp(variable), 'g'), variableValue);
    });
  }
  return content;
};

function injectPartnerNameInString(text: string): string {
  return text.replace(/{{\s*partnerName\s*}}/g, process.env.REACT_APP_PARTNER_NAME ?? 'partner');
}

function injectVariablesInRichText(terms: SanityRichText[]): SanityRichText[] {
  return terms.map(item => {
    if (item.text && item.marks?.includes('partnerName')) {
      return { ...item, text: process.env.REACT_APP_PARTNER_NAME };
    }

    if (item.text) {
      return { ...item, text: replaceVariablesWithFallback(item.text) };
    }

    if (item.children) {
      return { ...item, children: injectVariablesInRichText(item.children) };
    }

    return item;
  });
}

function prepareText(text: string) {
  const conditionStartRegex = /{{\s*#if\s*[a-zA-Z_.0-9 ]*}}/g;
  const conditionEndRegex = /{{\s*\/if\s*}}/g;
  const conditionStart = text.match(conditionStartRegex);
  const conditionEnd = text.match(conditionEndRegex);
  let content = text;
  conditionStart?.map(item => (content = content.replace(new RegExp(escapeRegExp(item), 'g'), '')));
  conditionEnd?.map(item => (content = content.replace(new RegExp(escapeRegExp(item), 'g'), '')));
  return content;
}

const verifyVariable = (condition: string) => {
  const data = getPartnerSettings();
  const variableName = condition.replaceAll('{{', '').replaceAll('}}', '').replace(/\s/g, '').replaceAll('#if', '');
  let variableValue;
  if (variableName.includes('.')) {
    const nestedVariables = variableName.split('.');
    //@ts-ignore
    variableValue = nestedVariables?.reduce((prev, variable) => prev?.[variable] ?? undefined, data);
  } else {
    //@ts-ignore
    variableValue = data[variableName];
  }
  return !!variableValue;
};

function checkCondition(
  text: string,
  isConditionTrue: boolean,
  isResolvingCondition = false,
): { content: string; resultOfTheCheck: boolean; resolvingCondition: boolean } {
  const conditionStartRegex = /{{\s*#if\s*[a-zA-Z_.0-9 ]*}}/g;
  const conditionEndRegex = /{{\s*\/if\s*}}/g;
  const fullConditionRegex = /({{\s*#if\s*[a-zA-Z_.0-9 ]*}})([^#]+?(?={{\s*\/if\s*}}))({{\s*\/if\s*}})/g;
  let fullConditionBlocks = text.match(fullConditionRegex);
  let resultOfTheCheck = isConditionTrue;
  let resolvingCondition = isResolvingCondition;
  let content = text;
  if (fullConditionBlocks && fullConditionBlocks.length > 0) {
    fullConditionBlocks.map(item => {
      const conditionStartBlocks = item.match(conditionStartRegex);
      conditionStartBlocks?.map(condition => {
        const result = verifyVariable(condition) ?? false;
        if (result) {
          const replacedText = prepareText(item);
          content = content.replaceAll(item, replacedText);
        } else {
          content = content.replaceAll(item, '');
        }
      });
    });
  }
  fullConditionBlocks = content.match(fullConditionRegex);
  if (fullConditionBlocks && fullConditionBlocks.length > 0) {
    return checkCondition(content, resultOfTheCheck, resolvingCondition);
  }

  const conditionEndBlocks = content.match(conditionEndRegex);
  let conditionStartBlocks = content.match(conditionStartRegex);

  if (resolvingCondition) {
    if (conditionEndBlocks && conditionEndBlocks.length > 0) {
      const conditionEnd = conditionEndBlocks[0];
      const textOfTheCurrentCondition = content.split(conditionEnd)?.[0];
      content = content.replace(conditionEnd, '');
      if (isConditionTrue) {
        content = content.replace(textOfTheCurrentCondition, prepareText(textOfTheCurrentCondition));
      } else {
        content = content.replace(textOfTheCurrentCondition, '');
      }
      resolvingCondition = false;
    } else if (conditionStartBlocks && conditionStartBlocks.length > 0) {
      const newConditionStart = conditionStartBlocks[0];
      const textOfTheCurrentCondition = content.split(newConditionStart)?.[0];
      content = content.replace(newConditionStart, '');
      if (isConditionTrue) {
        content = content.replace(textOfTheCurrentCondition, prepareText(textOfTheCurrentCondition));
      } else {
        content = content.replace(textOfTheCurrentCondition, '');
      }
    } else {
      if (resultOfTheCheck) {
        content = prepareText(content);
      } else {
        content = '';
      }
    }
  }

  conditionStartBlocks = content.match(conditionStartRegex);

  if (conditionStartBlocks && conditionStartBlocks.length > 0) {
    const conditionStart = conditionStartBlocks[0];
    resultOfTheCheck = verifyVariable(conditionStart);
    resolvingCondition = true;
    const textOfTheCurrentCondition = content.split(conditionStart)?.[1];
    content = content.replace(conditionStart, '');
    if (resultOfTheCheck) {
      content = content.replace(textOfTheCurrentCondition, prepareText(textOfTheCurrentCondition));
    } else {
      content = content.replace(textOfTheCurrentCondition, '');
    }
  }

  return { resultOfTheCheck, content, resolvingCondition };
}

function resolveConditionalBlock(terms: SanityRichText[]): SanityRichText[] {
  let isConditionTrue = false;
  let isResolvingCondition = false;
  return terms.map(item => {
    if (item.children) {
      const updatedChildren = item.children.map(child => {
        if (child.text) {
          const { resultOfTheCheck, content, resolvingCondition } = checkCondition(child.text, isConditionTrue, isResolvingCondition);
          isConditionTrue = resultOfTheCheck;
          isResolvingCondition = resolvingCondition;
          return { ...child, text: content };
        }
        return child;
      });
      return { ...item, children: updatedChildren };
    }

    return item;
  });
}

function replaceCustomSupplyContent(terms: SanityRichText[], data: SanityTermsAndConditions): SanityRichText[] {
  const partnerSettings = getPartnerSettings();
  const lang = i18next.language;
  const supplyTextVersion = partnerSettings.termsAndConditionsSupplyParagraphs;
  //@ts-ignore
  if (!supplyTextVersion || !data[supplyTextVersion]) {
    return terms;
  }
  //@ts-ignore
  let supplyText = data[supplyTextVersion][lang] ? data[supplyTextVersion][lang] : data[supplyTextVersion].en;
  supplyText = resolveConditionalBlock(supplyText);
  supplyText = injectVariablesInRichText(supplyText);

  const content = terms.map(item => {
    let isSupplyText = false;
    if (item.children) {
      return {
        ...item,
        children: item.children.map(child => {
          if (child.text && child.marks?.includes('supplyText')) {
            isSupplyText = true;
            return { ...child, text: '' };
          }
          return child;
        }),
        supplyText: isSupplyText,
      };
    }
    return item;
  });

  //@ts-ignore
  const supplyTextIndex = content.findIndex(item => item.supplyText);
  if (content[supplyTextIndex]) {
    const supplyTextChildIndex = content[supplyTextIndex].children?.findIndex(child => child.marks?.includes('supplyText'));
    const supplyTextFirstChild = content[supplyTextIndex].children;
    if (Array.isArray(supplyTextFirstChild)) {
      content[supplyTextIndex].children = [
        ...supplyTextFirstChild.slice(0, supplyTextChildIndex),
        ...supplyText,
        ...supplyTextFirstChild.slice(supplyTextChildIndex),
      ];
    }
  }
  return content;
}

function replaceRefundContent(terms: SanityRichText[], data: SanityTermsAndConditions): SanityRichText[] {
  const partnerSettings = getPartnerSettings();
  const lang = i18next.language;
  const isRefundPolicySet = !!partnerSettings.refundPolicy;
  const isRefundAllowed = partnerSettings.refundPolicy?.allowed;
  //@ts-ignore
  const isRefundAllowedText = isRefundAllowed ? data.refundYesText : data.refundYesText;
  if (!isRefundPolicySet || !isRefundAllowedText) {
    return terms;
  }
  let refundText = isRefundAllowedText[lang] ? isRefundAllowedText[lang] : isRefundAllowedText.en;
  refundText = resolveConditionalBlock(refundText);
  refundText = injectVariablesInRichText(refundText);

  const content = terms.map(item => {
    let isRefundText = false;
    if (item.children) {
      return {
        ...item,
        children: item.children.map(child => {
          if (child.text && child.marks?.includes('refundText')) {
            isRefundText = true;
            return { ...child, text: '' };
          }
          return child;
        }),
        refundText: isRefundText,
      };
    }
    return item;
  });

  //@ts-ignore
  const refundTextIndex = content.findIndex(item => item.refundText);
  if (content[refundTextIndex]) {
    const refundTextChildIndex = content[refundTextIndex].children?.findIndex(child => child.marks?.includes('refundText'));
    const refundTextFirstChild = content[refundTextIndex].children;
    if (Array.isArray(refundTextFirstChild)) {
      content[refundTextIndex].children = [
        ...refundTextFirstChild.slice(0, refundTextChildIndex),
        ...refundText,
        ...refundTextFirstChild.slice(refundTextChildIndex),
      ];
    }
  }
  return content;
}

const replaceCustomContents = (terms: SanityRichText[], data: SanityTermsAndConditions) => {
  let content = terms;
  content = resolveConditionalBlock(content);
  content = replaceCustomSupplyContent(content, data);
  content = replaceRefundContent(content, data);
  content = injectVariablesInRichText(content);
  return content;
};

const preparePartnerText = (terms: SanityRichText[]) => {
  let content = terms;
  content = resolveConditionalBlock(content);
  content = injectVariablesInRichText(content);
  return content;
};

const initialState: State = {
  loading: false,
  partnerTermsLoading: false,
  generalTerms: [],
  generalUpdateTime: '',
  generalTermsTitle: '',
  wechoooseTerms: [],
  wechoooseUpdateTime: '',
  wechoooseTermsTitle: '',
  partnerTerms: [],
  partnerUpdateTime: '',
  partnerTermsTitle: '',
  policyTerms: [],
  policyUpdateTime: '',
  policyTermsTitle: '',
  cookieTerms: [],
  cookieUpdateTime: '',
  cookieTermsTitle: '',
  additionalPartnerTerms: [],
};

const slice = createSlice({
  name: 'terms',
  initialState,
  reducers: {
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.loading = action.payload;
      return state;
    },
    setPartnerLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.partnerTermsLoading = action.payload;
      return state;
    },
    setGeneralTerms: (state: State, action: PayloadAction<SanityRichText[]>): State => {
      state.generalTerms = action.payload;
      return state;
    },
    setGeneralUpdateTime: (state: State, action: PayloadAction<string>): State => {
      state.generalUpdateTime = action.payload;
      return state;
    },
    setGeneralTermsTitle: (state: State, action: PayloadAction<string>): State => {
      state.generalTermsTitle = action.payload;
      return state;
    },
    setWeChoooseTerms: (state: State, action: PayloadAction<SanityRichText[]>): State => {
      state.wechoooseTerms = action.payload;
      return state;
    },
    setWeChoooseUpdateTime: (state: State, action: PayloadAction<string>): State => {
      state.wechoooseUpdateTime = action.payload;
      return state;
    },
    setWeChoooseTermsTitle: (state: State, action: PayloadAction<string>): State => {
      state.wechoooseTermsTitle = action.payload;
      return state;
    },
    setPartnerTerms: (state: State, action: PayloadAction<SanityRichText[]>): State => {
      state.partnerTerms = action.payload;
      return state;
    },
    setPartnerUpdateTime: (state: State, action: PayloadAction<string>): State => {
      state.partnerUpdateTime = action.payload;
      return state;
    },
    setPartnerTermsTitle: (state: State, action: PayloadAction<string>): State => {
      state.partnerTermsTitle = action.payload;
      return state;
    },
    setPolicyTerms: (state: State, action: PayloadAction<SanityRichText[]>): State => {
      state.policyTerms = action.payload;
      return state;
    },
    setPolicyUpdateTime: (state: State, action: PayloadAction<string>): State => {
      state.policyUpdateTime = action.payload;
      return state;
    },
    setPolicyTermsTitle: (state: State, action: PayloadAction<string>): State => {
      state.policyTermsTitle = action.payload;
      return state;
    },
    setCookieTerms: (state: State, action: PayloadAction<SanityRichText[]>): State => {
      state.cookieTerms = action.payload;
      return state;
    },
    setCookieUpdateTime: (state: State, action: PayloadAction<string>): State => {
      state.cookieUpdateTime = action.payload;
      return state;
    },
    setCookieTermsTitle: (state: State, action: PayloadAction<string>): State => {
      state.cookieTermsTitle = action.payload;
      return state;
    },
    setAdditionalPartnerTerms: (state: State, action: PayloadAction<PartnerTermsItem[]>): State => {
      state.additionalPartnerTerms = action.payload;
      return state;
    },
  },
});

const {
  setLoading,
  setPartnerLoading,
  setGeneralTerms,
  setWeChoooseTerms,
  setPolicyTerms,
  setCookieTerms,
  setGeneralUpdateTime,
  setWeChoooseUpdateTime,
  setPolicyUpdateTime,
  setCookieUpdateTime,
  setGeneralTermsTitle,
  setPolicyTermsTitle,
  setWeChoooseTermsTitle,
  setCookieTermsTitle,
  setPartnerTerms,
  setPartnerTermsTitle,
  setPartnerUpdateTime,
  setAdditionalPartnerTerms,
} = slice.actions;

export default slice.reducer;

export const loadGeneralTerms = (): AppThunk => async (dispatch): Promise<void> => {
  dispatch(setLoading(true));

  const lang = i18next.language;
  try {
    const data = await termsSanityApi.getTermsAndConditions(SANITY_TERMS.general);
    dispatch(setGeneralTerms(replaceCustomContents(data.content[lang] ? data.content[lang] : data.content.en, data)));
    dispatch(setGeneralUpdateTime(data._updatedAt));
    dispatch(setGeneralTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const loadWeChoooseTerms = (): AppThunk => async (dispatch): Promise<void> => {
  dispatch(setLoading(true));
  const lang = i18next.language;

  try {
    const data = await termsSanityApi.getTermsAndConditions(SANITY_TERMS.weChooose);
    dispatch(setWeChoooseTerms(replaceCustomContents(data.content[lang] ? data.content[lang] : data.content.en, data)));
    dispatch(setWeChoooseUpdateTime(data._updatedAt));
    dispatch(setWeChoooseTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const loadPartnerTerms = (partnershipId?: string): AppThunk => async (dispatch, getState): Promise<void> => {
  const lang = i18next.language;

  dispatch(setPartnerLoading(true));
  try {
    const appPartnershipId = getState().app.partnershipId;
    let data;

    if (partnershipId) {
      data = await termsSanityApi.getTermsAndConditions(partnershipId);
    } else if (appPartnershipId) {
      data = await termsSanityApi.getTermsAndConditions(appPartnershipId);
    } else {
      return;
    }

    const additionalData = await termsSanityApi.getPartnershipTerms(partnershipId ?? appPartnershipId ?? '');

    if (additionalData.length) {
      const additionalTerms = additionalData.map(
        (item): PartnerTermsItem => ({
          title: injectPartnerNameInString(item.title[lang] ? item.title[lang] : item.title.en),
          content: preparePartnerText(item.content[lang] ? item.content[lang] : item.content.en),
          updateTime: item._updatedAt,
          override: item.override ?? '',
          slug: item.slug?.current ?? '',
        }),
      );

      const newPartnerTerms = additionalTerms.find(set => set.override === 'partner');
      const newGeneralTerms = additionalTerms.find(set => set.override === 'general');
      const newPortalTerms = additionalTerms.find(set => set.override === 'portal');
      const newPrivacyTerms = additionalTerms.find(set => set.override === 'privacy');
      const newCookieTerms = additionalTerms.find(set => set.override === 'cookie');

      dispatch(setAdditionalPartnerTerms(additionalTerms));
      if (newPartnerTerms?.content) {
        dispatch(setPartnerTerms(newPartnerTerms.content));
        dispatch(setPartnerUpdateTime(newPartnerTerms.updateTime));
        dispatch(setPartnerTermsTitle(newPartnerTerms.title));
      } else if (data.title) {
        dispatch(setPartnerTerms(preparePartnerText(data.content[lang] ? data.content[lang] : data.content.en)));
        dispatch(setPartnerUpdateTime(data._updatedAt));
        dispatch(setPartnerTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
      }
      if (newGeneralTerms?.content) {
        dispatch(setGeneralTerms(newGeneralTerms.content));
        dispatch(setGeneralUpdateTime(newGeneralTerms.updateTime));
        dispatch(setGeneralTermsTitle(newGeneralTerms.title));
      }
      if (newPortalTerms?.content) {
        dispatch(setWeChoooseTerms(newPortalTerms.content));
        dispatch(setWeChoooseUpdateTime(newPortalTerms.updateTime));
        dispatch(setWeChoooseTermsTitle(newPortalTerms.title));
      }
      if (newPrivacyTerms?.content) {
        dispatch(setPolicyTerms(newPrivacyTerms.content));
        dispatch(setPolicyUpdateTime(newPrivacyTerms.updateTime));
        dispatch(setPolicyTermsTitle(newPrivacyTerms.title));
      }
      if (newCookieTerms?.content) {
        dispatch(setCookieTerms(newCookieTerms.content));
        dispatch(setCookieUpdateTime(newCookieTerms.updateTime));
        dispatch(setCookieTermsTitle(newCookieTerms.title));
      }
    } else if (data.title) {
      dispatch(setPartnerTerms(preparePartnerText(data.content[lang] ? data.content[lang] : data.content.en)));
      dispatch(setPartnerUpdateTime(data._updatedAt));
      dispatch(setPartnerTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
    }
    dispatch(setPartnerLoading(false));
  } catch {
    dispatch(setPartnerLoading(false));
  }
};

export const loadPolicyTerms = (): AppThunk => async (dispatch): Promise<void> => {
  const lang = i18next.language;

  dispatch(setLoading(true));
  try {
    const data = await termsSanityApi.getTermsAndConditions(SANITY_TERMS.policy);
    dispatch(setPolicyTerms(replaceCustomContents(data.content[lang] ? data.content[lang] : data.content.en, data)));
    dispatch(setPolicyUpdateTime(data._updatedAt));
    dispatch(setPolicyTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const loadCookieTerms = (): AppThunk => async (dispatch): Promise<void> => {
  const lang = i18next.language;

  dispatch(setLoading(true));
  try {
    const data = await termsSanityApi.getTermsAndConditions(SANITY_TERMS.cookie);
    dispatch(setCookieTerms(replaceCustomContents(data.content[lang] ? data.content[lang] : data.content.en, data)));
    dispatch(setCookieUpdateTime(data._updatedAt));
    dispatch(setCookieTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const loadAllTerms = (customPartnershipId?: string): AppThunk => async (dispatch, getState): Promise<void> => {
  const appPartnershipId = getState().app.partnershipId;
  dispatch(setLoading(true));
  const lang = i18next.language;

  try {
    let data = await termsSanityApi.getTermsAndConditions(SANITY_TERMS.general);
    dispatch(setGeneralTerms(replaceCustomContents(data.content[lang] ? data.content[lang] : data.content.en, data)));
    dispatch(setGeneralUpdateTime(data._updatedAt));
    dispatch(setGeneralTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
    data = await termsSanityApi.getTermsAndConditions(SANITY_TERMS.policy);
    dispatch(setPolicyTerms(replaceCustomContents(data.content[lang] ? data.content[lang] : data.content.en, data)));
    dispatch(setPolicyUpdateTime(data._updatedAt));
    dispatch(setPolicyTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
    data = await termsSanityApi.getTermsAndConditions(SANITY_TERMS.weChooose);
    dispatch(setWeChoooseTerms(replaceCustomContents(data.content[lang] ? data.content[lang] : data.content.en, data)));
    dispatch(setWeChoooseUpdateTime(data._updatedAt));
    dispatch(setWeChoooseTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
    dispatch(setLoading(false));

    if (appPartnershipId || customPartnershipId) {
      const data = await termsSanityApi.getTermsAndConditions(customPartnershipId ?? appPartnershipId ?? '');

      const additionalData = await termsSanityApi.getPartnershipTerms(customPartnershipId ?? appPartnershipId ?? '');
      if (additionalData.length) {
        const additionalTerms = additionalData.map(
          (item): PartnerTermsItem => ({
            title: injectPartnerNameInString(item.title[lang] ? item.title[lang] : item.title.en),
            content: preparePartnerText(item.content[lang] ? item.content[lang] : item.content.en),
            updateTime: item._updatedAt,
            override: item.override ?? '',
            slug: item.slug?.current ?? '',
          }),
        );

        const newPartnerTerms = additionalTerms.find(set => set.override === 'partner');
        const newGeneralTerms = additionalTerms.find(set => set.override === 'general');
        const newPortalTerms = additionalTerms.find(set => set.override === 'portal');
        const newPrivacyTerms = additionalTerms.find(set => set.override === 'privacy');
        const newCookieTerms = additionalTerms.find(set => set.override === 'cookie');

        dispatch(setAdditionalPartnerTerms(additionalTerms));
        if (newPartnerTerms?.title) {
          dispatch(setPartnerTerms(preparePartnerText(newPartnerTerms.content)));
          dispatch(setPartnerUpdateTime(newPartnerTerms.updateTime));
          dispatch(setPartnerTermsTitle(injectPartnerNameInString(newPartnerTerms.title)));
        } else if (data.title) {
          dispatch(setPartnerTerms(preparePartnerText(data.content[lang] ? data.content[lang] : data.content.en)));
          dispatch(setPartnerUpdateTime(data._updatedAt));
          dispatch(setPartnerTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
        }
        if (newGeneralTerms?.content) {
          dispatch(setGeneralTerms(preparePartnerText(newGeneralTerms.content)));
          dispatch(setGeneralUpdateTime(newGeneralTerms.updateTime));
          dispatch(setGeneralTermsTitle(injectPartnerNameInString(newGeneralTerms.title)));
        }
        if (newPortalTerms?.content) {
          dispatch(setWeChoooseTerms(preparePartnerText(newPortalTerms.content)));
          dispatch(setWeChoooseUpdateTime(newPortalTerms.updateTime));
          dispatch(setWeChoooseTermsTitle(injectPartnerNameInString(newPortalTerms.title)));
        }
        if (newPrivacyTerms?.content) {
          dispatch(setPolicyTerms(preparePartnerText(newPrivacyTerms.content)));
          dispatch(setPolicyUpdateTime(newPrivacyTerms.updateTime));
          dispatch(setPolicyTermsTitle(injectPartnerNameInString(newPrivacyTerms.title)));
        }
        if (newCookieTerms?.content) {
          dispatch(setCookieTerms(preparePartnerText(newCookieTerms.content)));
          dispatch(setCookieUpdateTime(newCookieTerms.updateTime));
          dispatch(setCookieTermsTitle(injectPartnerNameInString(newCookieTerms.title)));
        }
      } else if (data.content) {
        dispatch(setPartnerTerms(preparePartnerText(data.content[lang] ? data.content[lang] : data.content.en)));
        dispatch(setPartnerUpdateTime(data._updatedAt));
        dispatch(setPartnerTermsTitle(injectPartnerNameInString(data.title[lang] ? data.title[lang] : data.title.en)));
      }
    }

    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const overriddenTermsSelector = createSelector(
  (state: RootState) => state.terms,
  terms => {
    const overrideSet = terms.additionalPartnerTerms?.map(set => set.override);
    return {
      general: overrideSet?.includes('general'),
      portal: overrideSet?.includes('portal'),
      partner: overrideSet?.includes('partner'),
      privacy: overrideSet?.includes('privacy'),
      cookie: overrideSet?.includes('cookie'),
    };
  },
);
